import type { GameStateManager } from '../game/GameState';
import type { GameData, Researcher } from '../game/types';
import { getAnomalyDisplayName } from '../utils/AnomalyNameResolver';

export class ContextWindow {
  constructor(
    private container: HTMLElement,
    private gameState: GameStateManager,
    private gameData: GameData
  ) {
    this.update();
  }

  update(): void {
    const selected = this.gameState.getSelectedEntity();
    const strings = this.gameData.uiStrings;

    if (selected.type === null) {
      this.container.innerHTML = '';
      return;
    }

    if (selected.type === 'researcher') {
      this.renderResearcher(selected.id as string);
    } else if (selected.type === 'location') {
      this.renderLocation(selected.id as string);
    }
  }

  private renderResearcher(id: string): void {
    const researcher = this.gameState.getResearcher(id);
    if (!researcher) return;

    const strings = this.gameData.uiStrings;
    const state = this.gameState.getState();
    const location = researcher.location || 'control_room';
    const locationName = strings.locations?.[location] || location;
    const actionName = researcher.currentAction 
      ? strings.status?.[researcher.currentAction.toLowerCase()] || researcher.currentAction
      : strings.status?.idle || 'Idle';

    const progress = researcher.actionTotalTime > 0 
      ? (1 - researcher.actionTimer / researcher.actionTotalTime) * 100 
      : 0;

    const specialRules = researcher.data.special_rules.length > 0
      ? `<div class="special-rules">
           <div class="label">${strings.labels?.special_rules || 'Special Rules'}:</div>
           <div>${researcher.data.special_rules.map(rule => {
             const tooltipKey = `ability.${rule.toLowerCase()}`;
             return `<span class="special-ability" data-tooltip-key="${tooltipKey}">${rule}</span>`;
           }).join(', ')}</div>
         </div>`
      : '';

    const silverBullet = researcher.silverBullet
      ? (() => {
          const bullet = this.gameData.silverBullets.find(sb => sb.id === researcher.silverBullet);
          const displayName = bullet?.name || researcher.silverBullet;
          return `<div class="silver-bullet">
             <div class="label">${strings.labels?.silver_bullet || 'Silver Bullet'}:</div>
             <div data-tooltip-key="silver_bullet.${researcher.silverBullet}">${displayName}</div>
           </div>`;
        })()
      : '';

    const hasMemeticContagion = this.hasMemeticContagion(researcher);
    const displayedSanity = Math.ceil(researcher.currentSanity);
    const sanityText = `${displayedSanity}/${researcher.data.sanity}`;
    const warningSymbol = hasMemeticContagion ? ' ⚠️' : '';
    // Always include stat-value, conditionally add sanity-low
    const sanityClass = hasMemeticContagion ? 'stat-value sanity-low' : 'stat-value';

    this.container.innerHTML = `
      <div class="context-researcher">
        <div class="context-left">
          <div class="context-name">${researcher.data.name}</div>
          <div class="context-description">${researcher.data.description}</div>
          <div class="context-stats">
            <div class="stat-item">
              <span class="stat-label">${strings.labels?.sanity || 'Sanity'}:</span>
              <span class="${sanityClass}">${sanityText}${warningSymbol}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">${strings.labels?.location || 'Location'}:</span>
              <span class="stat-value">${locationName}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">${strings.labels?.current_action || 'Current Action'}:</span>
              <span class="stat-value">${actionName}</span>
            </div>
          </div>
          ${researcher.currentAction && researcher.actionTotalTime > 0 ? `
            <div class="progress-bar">
              <div class="progress-fill" style="width: ${progress}%"></div>
            </div>
          ` : ''}
        </div>
        <div class="context-right">
          ${specialRules}
          ${silverBullet}
        </div>
      </div>
    `;
  }

  private renderLocation(id: string): void {
    const locationId = id as LocationId;
    const anomaly = this.gameState.getAnomalyInCell(locationId);
    const strings = this.gameData.uiStrings;
    const locationName = strings.locations?.[locationId] || locationId;

    if (!anomaly) {
      this.container.innerHTML = `
        <div class="context-location">
          <div class="context-name">${locationName}</div>
          <div class="context-empty">${strings.labels?.cell_empty || 'Empty Cell'}</div>
        </div>
      `;
      return;
    }

    const propertyColors = strings.propertyColors || {};
    const displayName = getAnomalyDisplayName(anomaly, this.gameData);
    const knownProps = anomaly.knownProperties.map(p => {
      const symbol = strings.propertySymbols?.[p.toLowerCase()] || p;
      const name = strings.properties?.[p.toLowerCase()] || p;
      const propColor = propertyColors[p.toLowerCase()] || '#6bb0ff';
      return `<span class="property known property-with-name" data-tooltip-key="property.${p.toLowerCase()}" style="border-color: ${propColor};"><span class="property-symbol" style="color: ${propColor};">${symbol}</span><span class="property-name" style="color: ${propColor};">${name}</span></span>`;
    }).join('');
    const unknownCount = 3 - anomaly.knownProperties.length;
    const unknownProps = Array(unknownCount).fill(0).map(() => 
      `<span class="property unknown">${strings.properties?.unknown || '?'}</span>`
    ).join('');

    this.container.innerHTML = `
      <div class="context-location">
        <div class="context-name">${locationName}</div>
        <div class="context-anomaly">
          <div class="anomaly-name">${displayName}</div>
          <div class="anomaly-properties">
            <div class="label">${strings.labels?.properties || 'Properties'}:</div>
            <div>
              ${knownProps}
              ${unknownProps}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private hasMemeticContagion(researcher: Researcher): boolean {
    const maxSanity = researcher.data.sanity;
    const currentSanity = researcher.currentSanity;
    return currentSanity < maxSanity / 2;
  }
}


